// src/pages/CircleDiscovery.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "api";
import AppHeader from "../components/AppHeader";
import CirclesPanel from "../components/CirclesPanel";
import { selectMyCircles } from "../utils/circles";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";
import { toAbsolute } from "../utils/url";

// Small row for a search result
const ResultRow = ({ circle, onClick }) => {
  const name = circle?.name || "Untitled";

  // include any other possible fields you might have
  const rawUrl =
    circle?.image_url ||
    circle?.icon_url ||
    circle?.avatar_url ||
    "";

  // if rawUrl is truthy, absolutize it; otherwise use default immediately
  const imgSrc = rawUrl ? toAbsolute(rawUrl) : DEFAULT_CIRCLE_IMAGE;

  const memberCount =
    circle?.member_count ?? circle?.members_count ?? circle?.members?.length;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 !min-h-0 !min-w-0 px-3 py-2.5 hover:bg-gray-50 rounded-2xl text-left"
    >
      <img
        src={imgSrc}
        alt=""
        className="h-8 w-8 rounded-full object-cover"
        onError={(e) => {
          // force fallback if the URL 404s or is invalid
          if (e.currentTarget.src !== DEFAULT_CIRCLE_IMAGE) {
            e.currentTarget.src = DEFAULT_CIRCLE_IMAGE;
          }
        }}
      />
      <div className="min-w-0">
        <div className="font-medium text-sm truncate">{name}</div>
        {Number.isFinite(memberCount) && (
          <div className="text-xs text-gray-500">{memberCount} members</div>
        )}
      </div>
    </button>
  );
};

const CircleDiscovery = () => {
  const [joinedCircles, setJoinedCircles] = useState([]);
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);
  const myCircles = selectMyCircles(joinedCircles, me?.id);

  const [newCircleName, setNewCircleName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  // Load current user
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    api
      .get("/users/me", { headers })
      .then((r) => setMe(r.data))
      .catch(() => setMe(null))
      .finally(() => setLoadingMe(false));
  }, []);

  // Fetch circles user has joined
  useEffect(() => {
    const fetchJoined = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const endpoints = [
        "/users/me/groups",
        "/groups/joined",
        "/groups?joined=1",
        "/circles/joined",
      ];
      for (const url of endpoints) {
        try {
          const res = await api.get(url, { headers });
          const data = res?.data?.items ?? res?.data;
          if (Array.isArray(data)) {
            setJoinedCircles(
              data.map((c) => ({
                ...c,
                image_url:
                  toAbsolute(c.image_url || c.icon_url) ||
                  DEFAULT_CIRCLE_IMAGE,
              }))
            );
            return;
          }
        } catch {
          /* try next */
        }
      }
    };
    fetchJoined();
  }, []);

  const tryJoinCircle = async (circleId, meId, headers) => {
    const attempts = [
      { method: "post", url: `/groups/${circleId}/join/${meId}`, data: {} },
      { method: "post", url: `/groups/${circleId}/join`, data: {} },
      { method: "post", url: `/groups/join`, data: { group_id: circleId, user_id: meId } },
      { method: "post", url: `/circles/${circleId}/join`, data: { user_id: meId } },
      { method: "post", url: `/groups/${circleId}/members`, data: { user_id: meId } },
    ];
    for (const a of attempts) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api[a.method](a.url, a.data, { headers });
        return true;
      } catch (e) {
        const status = e?.response?.status;
        const msg = String(e?.response?.data?.detail || "");
        if (status === 409 || /already/i.test(msg)) return true;
      }
    }
    return false;
  };

  const handleCreateCircle = async () => {
    setCreateError("");
    if (!newCircleName.trim()) {
      setCreateError("Please enter a circle name.");
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      setCreateError("Please sign in to create a circle.");
      return;
    }
    setCreating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await api.post(
        "/groups",
        { name: newCircleName.trim() },
        { headers }
      );
      const circle = res?.data || {};
      const circleId = circle.id;

      if (circleId) {
        await tryJoinCircle(circleId, me?.id, headers);
        setJoinedCircles((prev) => [
          {
            ...circle,
            image_url:
              toAbsolute(circle.image_url || circle.icon_url) ||
              DEFAULT_CIRCLE_IMAGE,
          },
          ...prev,
        ]);
        navigate(`/circles/${circleId}`, { replace: true });
      }
      setNewCircleName("");
    } catch (e) {
      console.error(e);
      setCreateError(
        e?.response?.data?.detail || "Failed to create circle. Try again."
      );
    } finally {
      setCreating(false);
    }
  };

  // ---- Search ----
  const debouncedQuery = useMemo(() => search.trim(), [search]);

  useEffect(() => {
    // cancel previous in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    const q = debouncedQuery;
    if (!q) {
      setSearchResults([]);
      setSearchError("");
      setSearching(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      setSearching(true);
      setSearchError("");
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const endpoints = [
          (qq) => `/groups/search?q=${encodeURIComponent(qq)}`,
          (qq) => `/groups?search=${encodeURIComponent(qq)}&limit=20`,
          (qq) => `/circles/search?q=${encodeURIComponent(qq)}`,
          (qq) => `/groups?name=${encodeURIComponent(qq)}`,
        ];

        let found = null;
        for (const makeUrl of endpoints) {
          try {
            const url = makeUrl(q);
            const res = await api.get(url, {
              headers,
              signal: controller.signal,
            });
            const data = res?.data?.items ?? res?.data;
            if (Array.isArray(data)) {
              found = data;
              break;
            }
          } catch (e) {
            if (controller.signal.aborted) return;
            // try the next endpoint
          }
        }

        // Fallback: client-side filter from joined if server search fails
        if (!found) {
          const local = (joinedCircles || []).filter((c) =>
            (c?.name || "").toLowerCase().includes(q.toLowerCase())
          );
          found = local;
        }

        // Normalize + dedupe
        const normalized = (found || [])
          .map((c) => ({
            ...c,
            id: c.id ?? c.group_id ?? c.circle_id,
            name: c.name ?? c.title ?? c.slug,
            image_url:
              toAbsolute(c.image_url || c.icon_url) || DEFAULT_CIRCLE_IMAGE,
          }))
          .filter((c) => c && c.id);

        const byId = new Map();
        normalized.forEach((c) => byId.set(c.id, c));
        setSearchResults(Array.from(byId.values()));
      } catch (e) {
        if (controller.signal.aborted) return;
        setSearchError("Search failed. Please try again.");
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    };

    // small debounce (~200ms) without extra timers libs
    const t = setTimeout(run, 200);
    return () => clearTimeout(t);
  }, [debouncedQuery, joinedCircles]);

  const userForHeader = me
    ? { ...me, profile_pic_url: toAbsolute(me.profile_pic_url) || DEFAULT_PROFILE_IMAGE }
    : null;


  return (
    <div className="app-shell bg-blue-50">
      <header className="app-header">
        <AppHeader user={userForHeader} />
      </header>

      <main className="app-content w-full px-6 md:px-10 pt-[calc(64px+var(--safe-top))] md:mt-4 xl:px-16 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-10">
          {/* LEFT + CENTER */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Search card */}
            <div className="bg-white rounded-3xl mb-6 shadow-[0_8px_0px_rgba(37,99,235,0.96)] p-4 sm:p-6">
              <h1 className="text-lg font-bold mb-3 text-blue-600">
                Explore Circles
              </h1>
              <input
                type="text"
                placeholder="Search Circles"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm !h-9 !min-h-0 !min-w-0 !px-4 !py-2 rounded-2xl bg-white border border-gray-300 focus:border-blue-600 outline-none focus:ring-0"
                inputMode="search"
                autoComplete="off"
              />

              {/* Results */}
              {search && (
                <div className="mt-3 sm:mt-4 border border-gray-200 rounded-2xl overflow-hidden">
                  {searching && (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      Searching…
                    </div>
                  )}

                  {!searching && searchError && (
                    <div className="px-4 py-3 text-sm text-red-600">
                      {searchError}
                    </div>
                  )}

                  {!searching && !searchError && searchResults.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No circles found - you can create “{search}”
                    </div>
                  )}

                  {!searching && !searchError && searchResults.length > 0 && (
                    <div className="max-h-96 overflow-auto">
                      {searchResults.map((c) => (
                        <ResultRow
                          key={c.id}
                          circle={c}
                          onClick={() => navigate(`/circles/${c.id}`)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT RAIL */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky space-y-4">
              {/* Create Circle */}
              <div className="rounded-[2em] p-4 sm:p-6 bg-blue-600 text-white">
                <h2 className="text-lg font-semibold ml-2">
                  Meet me at third place
                </h2>

                <p className="mb-4 ml-2 text-sm opacity-90">
                  Start a new space for your thing, and your people!
                </p>

                <button
                  onClick={() => navigate("/circles/new")}
                  className="w-full rounded-3xl bg-white px-3 py-2 font-semibold text-blue-600 !min-h-0 !min-w-0 transition hover:bg-blue-50"
                >
                  Create Circle
                </button>
              </div>

              {/* Your Circles */}
              <div className="rounded-3xl bg-white p-4 sm:p-6">
                <CirclesPanel
                  joinedCircles={myCircles}
                  readOnly={false}
                  self
                  titleOverride="Your Circles"
                  frameless
                  maxIcons={12}
                />
              </div>
            </div>
          </aside>
        </div>
        <div className="app-footer-pad" />
      </main>
    </div>
  );
};

export default CircleDiscovery;
